import { useState, useEffect, type Dispatch, type SetStateAction } from "react";

export const MAX_WIDGETS_PER_PAGE = 13;

interface UseSetDataWithExportOptions<T> {
  isForExport?: boolean;
  setTotalWidgets?: ((total: number) => void) | null;
  setData: Dispatch<SetStateAction<T[]>> | ((data: T[]) => void);
  data: T[] | null | undefined;
  index?: number | null;
  setIsReadyToExport?: ((ready: boolean) => void) | null;
  error?: unknown;
  loading?: boolean;
}

interface UseSetDataWithExportReturn<T> {
  setDataWithExport: (dataArray: T[]) => void;
  setLoadedRowsCount: Dispatch<SetStateAction<number>>;
}

const useSetDataWithExport = <T>({
  isForExport,
  setTotalWidgets,
  setData,
  data,
  index,
  setIsReadyToExport,
  error,
  loading,
}: UseSetDataWithExportOptions<T>): UseSetDataWithExportReturn<T> => {
  const [loadedRowsCount, setLoadedRowsCount] = useState(0);

  const getTotalWidgetsByTotalRows = (totalRows: number) => {
    return Math.ceil(totalRows / MAX_WIDGETS_PER_PAGE);
  };

  const getCurrentRowsByIndex = (allRows: T[], index: number) =>
    allRows?.slice(
      index * MAX_WIDGETS_PER_PAGE,
      (index + 1) * MAX_WIDGETS_PER_PAGE
    );

  const setDataWithExport = (dataArray: T[]) => {
    if (isForExport && !!setTotalWidgets) {
      const totalWidgets = getTotalWidgetsByTotalRows(dataArray?.length);
      setTotalWidgets(totalWidgets || 1);
      const currentRows = getCurrentRowsByIndex(dataArray, index ?? 0);
      setData(currentRows);
    } else {
      setData(dataArray);
    }
  };

  useEffect(() => {
    if (!!data && loadedRowsCount === data?.length) {
      setIsReadyToExport?.(true);
    }
  }, [loadedRowsCount, data]);

  useEffect(() => {
    if (!!error) {
      setIsReadyToExport?.(true);
    }
  }, [error]);

  useEffect(() => {
    setIsReadyToExport?.(!loading);
  }, [loading]);

  return { setDataWithExport, setLoadedRowsCount };
};

export default useSetDataWithExport;
